import React, { Fragment, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import useForm from '@components/common/form/hooks/useForm';
import GenericHeader from '@components/common/pageHeader/GenericHeader';
import Button from '@components/common/button/Button';
import Input from '@components/common/form/Input';
import { methods } from '@components/common/form/constants/validator';
import { configs } from '@constants/configs/configs';
import { INPUT_TYPES } from '@components/common/form/constants/fieldTypes';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import accessCodeExpandedView from './fragments/AccessCodeExpandedView';
import accessCodeNonExpandedView from './fragments/AccessCodeNonExpandedView';
import { useAccessCode } from './hooks/useAccessCode';
import { ErrorModal } from '../../components/errorModal/ErrorModal';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import { RECAPTCHA_EXECUTE_ACTION } from '@constants/common.constants';
import { useReCaptchaErrorModal } from '@hooks/useReCaptchaErrorModal';
import { useReCaptcha } from '@components/common/form/recaptcha/useReCaptcha';
import useScrollToTop from '@hooks/useScrollToTop';

interface IAccessCodePageProps {
    environment: {
        exchangeName: string;
        location: {
            pathname: string;
        };
        accessCodeExpandedViewEnabled: boolean;
        stateCode: string;
        language: string;
    };
    authentication: boolean;
}

const AccessCodePage = (props: IAccessCodePageProps) => {
    const { states } = configs;
    const { errorMessage, errorTitleId, showError, showRecaptchaError, resetError } = useReCaptchaErrorModal();
    const ACCESS_CODE = 'accessCode';
    const SSN_CONSTANT = 'ssn';
    const { getReCaptchaToken, isCaptchaReady } = useReCaptcha();
    useScrollToTop();
    
    const {
        environment: {
            accessCodeExpandedViewEnabled,
            stateCode,
            language,
        },
    } = props;

    const { handleChange, values, errors, handleBlur, handleFocus, resetFields } = useForm(
        {},
        {
            accessCodeSelection: 'accessCode',
            accessCode: '',
            ssn: '',
            captchaValue: '',
        },
        {
            accessCode: {
                type: 'maxLength',
                validator: value => !methods.maxLength([value, VALIDATION_TYPES.ACCESSCODE_MAX_LENGTH]),
            },
            ssn: [
                { type: 'ssn', validator: value => !methods.ssn([value]) },
                { type: 'exactLength', validator: value => !methods.exactLength([value, VALIDATION_TYPES.SSN_LENGTH]) },
            ],
        },
    );

    const navigate = useNavigate();
    const { verifyAccessCode } = useAccessCode();

    useEffect(() => {
        resetFields(['accessCode']);
        sessionStorage.setItem('securityQuestionRefresh', 'false');
    }, [values && values.accessCodeSelection]);

    const formDisabled = !isCaptchaReady || (!values.ssn && !values.accessCode);

    const i18nReplacements = {
        accessCode: <FormattedMessage id="accounts.login_success.access_code" />,
        ssn: <FormattedMessage id="accounts.login_success.access_code" />,
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const target = e.target as HTMLFormElement;
        if (target?.ssn?.value) {
            target.ssn.value = values.ssn.replace(/-/g, '');
        }

        if (values.accessCodeSelection === ACCESS_CODE) {
            values.ssn = '';
        }

        if (values.accessCodeSelection === SSN_CONSTANT) {
            values.accessCode = '';
        }

        const recaptchaToken = await getReCaptchaToken(RECAPTCHA_EXECUTE_ACTION.REFERRAL_ACCESS_CODE);
        if (recaptchaToken === null) {
            showRecaptchaError();
            return;
        }
        values.recaptchaResponse = recaptchaToken;
        const result = await verifyAccessCode(values);
        if (!result) {
            return;
        }
        
        const { securityQuestions, encryptedActivationId, reference, errorMessage: apiErrorMessage, captchaInvalid } = result;
        if (securityQuestions) {
            navigate(configs.routes.DYNAMIC_SECURITY_QUESTIONS, {
                state: { randomQuestions: securityQuestions, encryptedActivationId, reference, accessCodeValues:  values},
            });
            return;
        }
        if (captchaInvalid) {
            showRecaptchaError();
            return;
        }
        if (apiErrorMessage) {
            showError(apiErrorMessage);
        }
    };

    return (
        <Fragment>
            <GenericHeader text="accounts.login_success.header_1" tag="h1" />
            <div className="grid-container padding-0">
                <div className="grid-row padding-0">
                    <div className="gi-panel desktop:grid-col-fill tablet:grid-col-fill mobile-lg:grid-col-12 grid-col-12">
                        <div className="desktop:grid-col-10 tablet:grid-col-10 mobile-lg:grid-col-12 grid-col-12 section__content">
                            <form name="access-form" onSubmit={handleSubmit}>
                                <ConditionalRendering isTrue={!accessCodeExpandedViewEnabled}>
                                    {accessCodeNonExpandedView({
                                        values,
                                        i18nReplacements,
                                        handleChange,
                                        handleBlur,
                                        handleFocus,
                                        errors,
                                    })}
                                </ConditionalRendering>
                                <ConditionalRendering isTrue={accessCodeExpandedViewEnabled}>
                                    {accessCodeExpandedView({
                                        values,
                                        i18nReplacements,
                                        handleChange,
                                        handleBlur,
                                        handleFocus,
                                        errors,
                                        stateCode,
                                    })}
                                </ConditionalRendering>
                                <Input
                                    label={{
                                        text: 'common.no_text',
                                        isInputRequired: false,
                                        labelClasses: [''],
                                        wrapperClasses: [''],
                                    }}
                                    inputField={{
                                        name: 'csrftoken',
                                        inputClasses: [''],
                                        type: 'hidden',
                                        value: window.csrf_token,
                                    }}
                                />
                                <Button
                                    {...{
                                        id: 'submit',
                                        name: 'submit',
                                        type: 'submit',
                                        outerClass: ['margin-b-20'],
                                        classes: ['gi-button__primary'],
                                        link_type: INPUT_TYPES.SUBMIT,
                                        btnText: 'button.submit',
                                        disabled: formDisabled,
                                        handleClick: handleSubmit
                                    }}
                                />
                            </form>
                            <ConditionalRendering isTrue={stateCode === states.IDAHO}>
                                <FormattedMessage
                                    id="accounts.accessCode.id_help_text"
                                    values={{
                                        strong: chunks => {
                                            return <strong>{chunks}</strong>;
                                        },
                                    }}
                                />
                            </ConditionalRendering>
                        </div>
                    </div>
                    {/*<StartApplication />*/}
                </div>
            </div>
            <ConditionalRendering isTrue={errorMessage.length > 0}>
                <ErrorModal onClose={resetError} errorText={errorMessage} titleId={errorTitleId} />
            </ConditionalRendering>
        </Fragment>
    );
};

export default AccessCodePage;
